import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import {
  GitHubRepositoryResult,
  GitHubRepositorySelection,
  isGitHubBranchName,
  isGitHubRepositoryName,
  readGitHubRepositoryResult,
} from './IntegrationConfig_GitHub';

//The branch rules shared by the GitHub checks that judge branches (PLAN-FBL-003): branch protection, force push, pull
//request review and CODEOWNERS. A check adds its rule type, its rule and branch results, and its texts.

//The API's GitHubBranchRules limits
export const gitHubMaxRulesPerList = 10;
export const gitHubMaxRepositoryRules = 200;

//
// Rules: a branch name (no patterns, PLAN-FBL-002 amendment 1); a check adds what the branch must do
//
export abstract class GitHubBranchRule {
  //empty: the repository's default branch
  branch: string = '';

  abstract clone(): this;

  abstract isEqual(other: this): boolean;
}

export const areBranchRulesEqual = <TRule extends GitHubBranchRule>(a: TRule[], b: TRule[]): boolean =>
  a.length === b.length && a.every((rule, index) => rule.isEqual(b[index]));

//The problems of one rule list, as the API's GitHubBranchRules.Validate sees them; empty when valid
export const getBranchRuleProblems = (rules: GitHubBranchRule[], t: TFunction<string[]>): string[] => {
  const problems: string[] = [];

  if (rules.length === 0) {
    problems.push(t('integrations:GitHub_BranchProtection.Rules.Required'));

    return problems;
  }

  if (rules.length > gitHubMaxRulesPerList) {
    problems.push(t('integrations:GitHub_BranchProtection.Rules.TooMany', { max: gitHubMaxRulesPerList }));
  }

  const values = rules.map((rule) => rule.branch.trim());
  const duplicates = values.filter((value, index) => values.indexOf(value) !== index);
  for (const value of new Set(duplicates)) {
    problems.push(t('integrations:GitHub_BranchProtection.Rules.Duplicate', { branch: value }));
  }

  for (const value of values) {
    if (value && !isGitHubBranchName(value)) {
      problems.push(t('integrations:GitHub_BranchProtection.Rules.InvalidBranch', { branch: value }));
    }
  }

  return problems;
};

//A rule's branch as shown: the value, or "default branch" when empty
export const getBranchRuleText = (rule: { branch: string }, t: TFunction<string[]>): string =>
  rule.branch.trim() ? rule.branch.trim() : t('integrations:GitHub_BranchProtection.Result.DefaultBranch');

//
// Config: the API's GitHubBranchRulesConfig<TRule>
//
export abstract class GitHubBranchRulesConfig<TRule extends GitHubBranchRule> extends IntegrationConfigBase {
  //which repositories; its branch is legacy, cleared when read
  selection: GitHubRepositorySelection = new GitHubRepositorySelection();

  //a check's from() sets one new rule when the stored config has none
  defaultRules: TRule[] = [];

  //repositories with branch rules of their own instead of the default rules; the name without the owner, compared
  //case-insensitively
  repositoryRules: { repository: string; rules: TRule[] }[] = [];

  createChecklistItemPerRepository: boolean = true;

  //a rule of this check with its defaults
  protected abstract newRule(): TRule;

  //A rule of this check from a stored or evidence value: the new rule's fields, each taken from the value when it has
  //the same type there
  createRule(stored?: unknown): TRule {
    const rule = this.newRule();
    const raw = (stored ?? {}) as Record<string, unknown>;
    const target = rule as unknown as Record<string, unknown>;
    for (const key of Object.keys(target)) {
      if (typeof raw[key] === typeof target[key]) target[key] = raw[key];
    }

    return rule;
  }

  //The fields every branch rules config stores; a check's from() reads its own fields and the legacy shapes
  protected readBranchRules(raw: {
    selection?: unknown;
    defaultRules?: unknown[] | null;
    repositoryRules?: unknown[];
    createChecklistItemPerRepository?: boolean;
  }): void {
    this.selection = GitHubRepositorySelection.from(raw.selection);
    this.selection.branch = '';
    this.defaultRules = Array.isArray(raw.defaultRules)
      ? raw.defaultRules.map((rule) => this.createRule(rule))
      : [this.newRule()];
    this.repositoryRules = Array.isArray(raw.repositoryRules)
      ? raw.repositoryRules.map((stored) => {
          const entry = (stored ?? {}) as { repository?: string; rules?: unknown[] };

          return {
            repository: entry.repository ?? '',
            rules: Array.isArray(entry.rules) ? entry.rules.map((rule) => this.createRule(rule)) : [],
          };
        })
      : [];
    this.createChecklistItemPerRepository =
      raw.createChecklistItemPerRepository ?? this.createChecklistItemPerRepository;
  }

  //A copy to change; a check with fields that are not values overrides it to copy them too
  clone(): this {
    const copy = Object.assign(Object.create(Object.getPrototypeOf(this)), this) as this;
    copy.selection = GitHubRepositorySelection.from(this.selection);
    copy.defaultRules = this.defaultRules.map((rule) => rule.clone());
    copy.repositoryRules = this.repositoryRules.map((entry) => ({
      repository: entry.repository,
      rules: entry.rules.map((rule) => rule.clone()),
    }));

    return copy;
  }

  getRulesFor(repository: string): { rules: TRule[]; hasOwnRules: boolean } {
    const own = this.repositoryRules.find(
      (entry) => entry.repository.trim().toLowerCase() === repository.trim().toLowerCase(),
    );

    return own ? { rules: own.rules, hasOwnRules: true } : { rules: this.defaultRules, hasOwnRules: false };
  }

  //gives every named repository these rules as its own
  setRulesFor(repositories: string[], rules: TRule[]): void {
    this.restoreDefaultFor(repositories);
    for (const repository of repositories) {
      this.repositoryRules.push({ repository, rules: rules.map((rule) => rule.clone()) });
    }
  }

  //the named repositories follow the default rules again
  restoreDefaultFor(repositories: string[]): void {
    const names = new Set(repositories.map((name) => name.trim().toLowerCase()));
    this.repositoryRules = this.repositoryRules.filter((entry) => !names.has(entry.repository.trim().toLowerCase()));
  }

  getProblems(t: TFunction<string[]>): string[] {
    const problems = [...this.selection.getProblems(t), ...getBranchRuleProblems(this.defaultRules, t)];

    if (this.repositoryRules.length > gitHubMaxRepositoryRules) {
      problems.push(
        t('integrations:GitHub_BranchProtection.Rules.TooManyRepositories', { max: gitHubMaxRepositoryRules }),
      );
    }

    const seen = new Set<string>();
    for (const entry of this.repositoryRules) {
      const name = entry.repository.trim();
      if (seen.has(name.toLowerCase())) {
        problems.push(t('integrations:GitHub_BranchProtection.Rules.DuplicateRepository', { name }));
      }
      seen.add(name.toLowerCase());

      if (!isGitHubRepositoryName(name)) {
        problems.push(t('integrations:GitHubBaseline.Selection.InvalidNames', { names: name }));
      }

      problems.push(...getBranchRuleProblems(entry.rules, t).map((problem) => `${name}: ${problem}`));
    }

    return problems;
  }

  //a check with fields of its own overrides it and calls this one
  isEqual(other: GitHubBranchRulesConfig<TRule>): boolean {
    const sameRepositoryRules =
      this.repositoryRules.length === other.repositoryRules.length &&
      this.repositoryRules.every((entry) => {
        const match = other.repositoryRules.find(
          (o) => o.repository.trim().toLowerCase() === entry.repository.trim().toLowerCase(),
        );

        return match !== undefined && areBranchRulesEqual(entry.rules, match.rules);
      });

    return (
      this.selection.isEqual(other.selection) &&
      areBranchRulesEqual(this.defaultRules, other.defaultRules) &&
      sameRepositoryRules &&
      this.createChecklistItemPerRepository === other.createChecklistItemPerRepository
    );
  }
}

//The repository-level problem codes every branch rules check shares
export const getBranchRulesRepositoryProblemText = (code: string, t: TFunction<string[]>): string => {
  switch (code) {
    case 'RepositoryNotFound':
      return t('integrations:GitHub_BranchProtection.Problems.RepositoryNotFound');
    default:
      return code;
  }
};

//
// Evidence: repository > rule > branch, the API's generic bases; a check adds its values to the rule and branch results
//
export class GitHubBranchResult {
  name: string = '';

  //stable problem codes; empty when passed
  problems: string[] = [];

  passed: boolean = false;
}

export class GitHubBranchRuleResult<TBranch extends GitHubBranchResult> {
  //the rule's value as configured; empty is the default branch
  branch: string = '';

  passed: boolean = false;

  //the rule's one branch
  branches: TBranch[] = [];
}

export class GitHubBranchRulesRepository<
  TRuleResult extends GitHubBranchRuleResult<GitHubBranchResult>,
> extends GitHubRepositoryResult {
  hasOwnRules: boolean = false;

  rules: TRuleResult[] = [];
}

export abstract class GitHubBranchRulesEvidence<TRuleResult extends GitHubBranchRuleResult<GitHubBranchResult>>
  implements IIntegrationEvidence
{
  pass: boolean = false;

  //the repository selection this run was judged by
  selection: GitHubRepositorySelection | undefined = undefined;

  organization: string = '';

  evaluatedAtUtc: Date = new Date();

  repositoriesEvaluated: number = 0;

  repositoriesFailed: number = 0;

  isCapped: boolean = false;

  repositories: GitHubBranchRulesRepository<TRuleResult>[] = [];

  abstract toMarkdownReport(t: TFunction<string[]>): string;
}

//The rule result type of a branch rules evidence type
export type GitHubBranchRuleResultOf<
  TEvidence extends GitHubBranchRulesEvidence<GitHubBranchRuleResult<GitHubBranchResult>>,
> = TEvidence['repositories'][number]['rules'][number];

//Reads the shared evidence fields into the check's evidence. readBranch and readRule create the check's branch and
//rule results with the check's own values; the shared values are read here. The nested shape only (PLAN-FBL-002): a
//result from before it has no rules per repository and shows as "test again".
export const readBranchRulesEvidence = <
  TEvidence extends GitHubBranchRulesEvidence<TRuleResult>,
  TRuleResult extends GitHubBranchRuleResult<TBranch>,
  TBranch extends GitHubBranchResult,
>(
  target: TEvidence,
  raw: string | undefined,
  readBranch: (item: Record<string, unknown>) => TBranch,
  readRule: (item: Record<string, unknown>) => TRuleResult,
): TEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  if (
    Array.isArray(rawEvidence.repositories) &&
    rawEvidence.repositories.some((item: Record<string, unknown>) => !Array.isArray(item.rules))
  ) {
    return undefined;
  }

  const toBranch = (item: Record<string, unknown>): TBranch => {
    const branch = readBranch(item);
    branch.name = typeof item.name === 'string' ? item.name : '';
    branch.problems = Array.isArray(item.problems) ? item.problems.filter((code) => typeof code === 'string') : [];
    branch.passed = item.passed === true;

    return branch;
  };

  const toRule = (item: Record<string, unknown>): TRuleResult => {
    const rule = readRule(item);
    rule.branch = typeof item.branch === 'string' ? item.branch : '';
    rule.passed = item.passed === true;
    rule.branches = Array.isArray(item.branches)
      ? item.branches.map((branch: Record<string, unknown>) => toBranch(branch))
      : [];

    return rule;
  };

  const evidence = target;
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.organization = rawEvidence.organization ?? '';
  evidence.repositoriesEvaluated = rawEvidence.repositoriesEvaluated ?? 0;
  evidence.repositoriesFailed = rawEvidence.repositoriesFailed ?? 0;
  evidence.pass = rawEvidence.pass ?? false;
  evidence.selection = rawEvidence.selection ? GitHubRepositorySelection.from(rawEvidence.selection) : undefined;
  if (evidence.selection) evidence.selection.branch = '';
  evidence.repositories = Array.isArray(rawEvidence.repositories)
    ? rawEvidence.repositories.map((item: Record<string, unknown>) => {
        const repository = readGitHubRepositoryResult(new GitHubBranchRulesRepository<TRuleResult>(), item);
        repository.hasOwnRules = item.hasOwnRules === true;
        repository.rules = Array.isArray(item.rules)
          ? item.rules.map((rule: Record<string, unknown>) => toRule(rule))
          : [];

        return repository;
      })
    : [];

  return evidence;
};

//The markdown report of a branch rules check: the summary lines, then a row per branch with what was found and the
//result. The check gives its heading, its problem texts and its found text.
export const getBranchRulesMarkdownReport = <TBranch extends GitHubBranchResult>(
  evidence: GitHubBranchRulesEvidence<GitHubBranchRuleResult<TBranch>>,
  heading: string,
  getProblemText: (code: string, branch?: TBranch, rule?: GitHubBranchRuleResult<TBranch>) => string,
  getFoundText: (branch: TBranch) => string,
  t: TFunction<string[]>,
): string => {
  const sb: string[] = [];
  const pass = t('integrations:GitHubBaseline.Result.Pass');

  sb.push(`## ${heading}`);
  sb.push(
    `- ${t('integrations:GitHubBaseline.Report.CountLine', {
      evaluated: evidence.repositoriesEvaluated,
      failed: evidence.repositoriesFailed,
    })}`,
  );
  if (evidence.isCapped) {
    sb.push(`- ${t('integrations:GitHubBaseline.Report.Capped')}`);
  }
  sb.push(
    `- ${t('integrations:GitHubBaseline.Report.OutcomeLine', {
      outcome: evidence.pass ? pass : t('integrations:GitHubBaseline.Result.Fail'),
    })}`,
  );
  sb.push('');
  sb.push(
    `| ${t('integrations:GitHubBaseline.Columns.Repository')} | ${t('integrations:GitHubBaseline.Columns.Rule')} | ${t(
      'integrations:GitHubBaseline.Columns.Branch',
    )} | ${t('integrations:GitHubBaseline.Columns.Found')} | ${t('integrations:GitHubBaseline.Columns.Result')} |`,
  );
  sb.push('| --- | --- | --- | --- | --- |');
  for (const repository of evidence.repositories) {
    if (repository.problems.length > 0) {
      sb.push(`| ${repository.name} | | | | ${repository.problems.map((code) => getProblemText(code)).join(' ')} |`);
    }
    for (const rule of repository.rules) {
      const ruleText = getBranchRuleText(rule, t);
      for (const branch of rule.branches) {
        const result = branch.passed
          ? pass
          : branch.problems.map((code) => getProblemText(code, branch, rule)).join(' ');
        sb.push(`| ${repository.name} | ${ruleText} | ${branch.name} | ${getFoundText(branch)} | ${result} |`);
      }
    }
  }

  return sb.join('\n');
};
